import { useState } from 'react';
import { i18n, staffFeatureKeys, type StaffFeatures } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;
export interface PartnerFeatureUser {
  id: string;
  displayName: string;
  email: string;
  roles: { role: string }[];
  staffSkuCreate?: boolean | null;
  staffSkuEdit?: boolean | null;
  staffSkuPhotoUpload?: boolean | null;
}

/** Feature-only editor: never submits roles, owner flags or organization scopes. */
export function PartnerFeaturesEditor({ users }: { users: PartnerFeatureUser[] }) {
  const [accounts, setAccounts] = useState(users);
  const [selected, setSelected] = useState('');
  const [features, setFeatures] = useState<StaffFeatures | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const save = async () => {
    if (!features) return;
    setBusy(true);
    setSaved(false);
    setError(false);
    try {
      const updated = await api<PartnerFeatureUser>(`/staff-access/partner-features/${selected}`, {
        method: 'PUT',
        body: { features },
      });
      setAccounts(accounts.map((a) => (a.id === selected ? updated : a)));
      setSaved(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="space-y-3" aria-label={t('staff.partner_features.title')}>
      <h3 className="text-lg font-semibold">{t('staff.partner_features.title')}</h3>
      <p>{t('staff.partner_features.hint')}</p>
      <label>
        {t('staff.account')}{' '}
        <select
          value={selected}
          disabled={busy}
          aria-label={t('staff.partner_features.account')}
          className="rounded border p-2"
          onChange={(e) => {
            const account = accounts.find((a) => a.id === e.target.value);
            setSelected(e.target.value);
            setSaved(false);
            setError(false);
            setFeatures(
              account
                ? {
                    skuCreate: account.staffSkuCreate ?? null,
                    skuEdit: account.staffSkuEdit ?? null,
                    skuPhotoUpload: account.staffSkuPhotoUpload ?? null,
                  }
                : null,
            );
          }}
        >
          <option value="">{t('staff.choose')}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.displayName} ({a.email}) — {a.roles.map((r) => t(`role.${r.role}`)).join(', ')}
            </option>
          ))}
        </select>
      </label>
      {features && (
        <fieldset disabled={busy} className="space-y-2">
          {staffFeatureKeys.map((key) => (
            <label key={key} className="block">
              {t(`staff.features.${key}`)}{' '}
              <select
                aria-label={t(`staff.features.${key}`)}
                className="rounded border p-2"
                value={features[key] == null ? 'default' : String(features[key])}
                onChange={(e) => {
                  setFeatures({
                    ...features,
                    [key]: e.target.value === 'default' ? null : e.target.value === 'true',
                  });
                  setSaved(false);
                }}
              >
                <option value="default">{t('staff.features.default')}</option>
                <option value="true">{t('staff.features.allow')}</option>
                <option value="false">{t('staff.features.deny')}</option>
              </select>
            </label>
          ))}
          <p>{t('staff.features.hint')}</p>
          <p>{t('staff.sessions')}</p>
          <button className="rounded border px-4 py-2" onClick={() => void save()}>
            {t(busy ? 'staff.saving' : 'staff.save')}
          </button>
          {saved && <p role="status">{t('staff.saved')}</p>}
          {error && <p role="alert">{t('staff.save_error')}</p>}
        </fieldset>
      )}
    </section>
  );
}
